<template lang="pug">
    div 
        section.wam-content.full-width(v-if="allocationArray.length == 0 && !loading")
            section.l-flex-column.is-empty.zero-state
                p.zero-state-message Oh no 😕
                    |
                    br
                    | {{ zeroStateMsg }}
        
        .dashboard-top100-wrapper
            Spinner(v-if="loading" width="100" height="100")
              

            .dashboard-table-wrapper
                .dashboard-search-wrapper(v-if="!loading")
                    Search(
                        :query="queryFilter"
                        :queryType="queryTypeFilter"
                        :types="SearchTypes"
                        :sortByScore="false"
                        allTypeSearch
                        @queryChange="queryFilter = $event"
                        @queryTypeChange="queryTypeFilter = $event"
                        @filterChange="handleFilterQueryChange"
                        @reset="componentHandleQueryReset"
                    ) 
                ElTable.dashboard-table.clickable-rows(
                    v-if="!loading"
                    :data="paginatedData"
                    ref="tableData"
                    row-key="id"
                    :expand-row-keys="expandRowKeys"
                    :default-sort="{ order: sortOrder, prop: sortBy }"
                    header-row-class-name="dashboard-table-header-row"
                    header-cell-class-name="dashboard-table-header-cell"
                    :row-class-name="setRowClassName"
                    cell-class-name="dashboard-tb-cell"
                    empty-text="We could not find any match for your search"
                    @row-click="handleRowClick"
                    @sort-change="componentHandleSortChange"
                    :max-height="getTableMaxHeight()"
                    :style="{ minHeight: getTableMaxHeight() + 'px', width: '100%'}"
                )
                
                    //- ElTableColumn(type="selection" width="55" :selectable="isRowSelectable")

                    ElTableColumn(type="expand" width="1" align="right")
                        template(slot-scope="scope")
                            .dashboard-tb-row-expended-wrapper
                                Spinner(v-if="scope.row.loading" width="100" height="100")
                                AllocationCharts(
                                    v-if="scope.row.allocation > 0 && scope.row.allocations"
                                    v-bind="{ linearName, doughnutName }"
                                    :allocationData="scope.row.allocations"
                                    :allocationTimeUnit="scope.row.timeUnit"
                                )
                                p(v-if="!scope.row.allocation") There is no available data on the selected {{ rowName }}

                    ElTableColumn(label="Id" prop="id" width="200" sortable)
                    
                    ElTableColumn(
                        label="Label"
                        prop="label"
                        :sort-method="sortColumnBy('label')"
                        sortable
                    )
                    
                    ElTableColumn(
                        label="Allocation",
                        prop="allocation"
                        align="left"
                        width="300"
                        sortable
                    )
                        template(slot-scope="scope") {{ formatNumber(scope.row.allocation) }}
                    
                    ElTableColumn(
                        label="Alert"
                        align="left"
                        width="200"
                    )
                        template(
                           slot-scope="scope" 
                           v-if="scope.row.alerts.length > 0"
                        ) 
                            el-popover(
                                placement="bottom"
                                width="300"
                                trigger="manual"
                                :value="alertPoperVisible === scope.row.id"
                            )   
                                ul
                                    li.is-truncate(
                                        v-for="alert in scope.row.alerts" 
                                        :key="alert.id"
                                    ) {{ alert.id }} - {{ alert.label }}
                                span.small-text Click to view alert(s) details
                                .alert-tag(
                                    slot="reference"
                                    @mouseover="alertPoperVisible = scope.row.id"
                                    @mouseleave="alertPoperVisible = null"
                                    @click.stop="goToAlertTable(scope.row.id)"
                                ) {{ scope.row.alerts.length }}

                            el-popover(
                                v-if="getTriggeredAlerts(scope.row).length > 0"
                                placement="bottom"
                                width="300"
                                trigger="manual"
                                :value="alertTriggeredPoperVisible === scope.row.id"
                            )   
                                h2.alerts-poper-title Alert(s) triggered
                                ul
                                    li.is-truncate(
                                        v-for="alert in getTriggeredAlerts(scope.row)" 
                                        :key="alert.id"
                                    ) {{ alert.id }} - {{ alert.label }}
                                span.small-text Click to view alert(s) details
                                .alert-tag.triggered(
                                    slot="reference"
                                    @mouseover="alertTriggeredPoperVisible = scope.row.id"
                                    @mouseleave="alertTriggeredPoperVisible = null"
                                    @click.stop="goToAlertTable(scope.row.id, true)"
                                ) {{ getTriggeredAlerts(scope.row).length }}

                    
                    ElTableColumn(
                        v-if="hasUserAccessToMonitoring"
                        width="200"
                        class-name="cell-align-right"
                    )
                        template(slot-scope="scope")
                            el-button(
                                @click.stop="openAlertForm(scope.row.id)"
                                v-if="!scope.row.deleted && isAlertCreationAllowed"
                                class="reveal-on-hover"
                                size="small"
                            ) Create Alert
                            
                            el-button(
                                v-if="!scope.row.deleted"
                                @click.stop="goToDefinition(scope.row.id)"
                                type="image"
                                size="small"
                            )
                                svg-icon(
                                    :title="`go to ${rowName}`"
                                    name='go' 
                                    size="small"
                                )


            ElPagination(
                v-if="!loading"
                background
                layout="prev, pager, next, sizes"
                :total="filteredData.length"
                :page-size.sync="pageSize"
                :current-page.sync="currentPage"
                :page-sizes="[5, 10, 25, 50, 100]"
            )
</template>

<script>
import { isEmpty, keyBy } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'

import { formatNumber } from '@/utils/helpers'
import tablePaginationMixin from '@/mixins/tablePaginationMixin'
import tableOrderMixin from '@/mixins/tableOrderMixin'

import AllocationCharts from '@/components/dashboard/AllocationCharts'
import Search from '@/components/common/Search'
import Spinner from '@/components/common/Spinner'

export default {
    name: 'AllocationTable',

    mixins: [tablePaginationMixin, tableOrderMixin],

    components: {
        AllocationCharts,
        Search,
        Spinner
    },

    props: {
        allocations: {
            type: Object,
            default: () => ({})
        },
        allocationsDetails: {
            type: Object,
            default: () => ({})
        },
        alerts: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        },
        rowName: {
            type: String,
            default: 'row'
        },
        showDeleted: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            expandRowKeys: [],
            sortBy: 'allocation',
            SearchTypes: { id: 'id', label: 'label' },
            queryFilter: null,
            queryTypeFilter: null,
            queryFilterFn: data => data,
            alertPoperVisible: null,
            alertTriggeredPoperVisible: null
        }
    },

    computed: {
        ...mapState('account', {
            accountId: state => state.accountId
        }),

        ...mapGetters('account', ['getAccessToMonitoring', 'getCreateAlertsAllowed']),
        ...mapGetters('data/customTaxonomies', ['getWamCustomTaxoIdBySegmentId']),

        allocationArray() {
            return Object.values(this.allocations)
                .map(({ id, value, allocations, loading, timeUnit }) => ({
                    allocation: value,
                    allocations,
                    id,
                    label: this.allocationsDetails[id]?.label || 'Deleted item',
                    deleted: !this.allocationsDetails[id],
                    loading,
                    timeUnit,
                    alerts: this.alerts[id] || []
                }))
                .filter(i => this.showDeleted || !i.deleted)
        },

        filteredData() {
            return this.allocationArray.filter((allocation) => {
                if(this.queryFilter !== null){
                    if (this.queryTypeFilter === null) {
                        return allocation.label?.toLowerCase().includes(this.queryFilter.toLowerCase()) 
                        || (`${allocation.id && allocation?.id}`.includes(this.queryFilter))
                    } else if (this.queryTypeFilter === 'id') {
                        return allocation?.id && `${allocation.id}`.startsWith(this.queryFilter)
                    } else if (this.queryTypeFilter === 'label') {
                        return allocation.label?.toLowerCase().includes(this.queryFilter.toLowerCase())
                    }
                }
                return allocation
            })
        },

        sortedData() {
            const rows = [...this.filteredData]
            return rows.sort(this.sortColumnBy(this.sortBy))
        },

        paginatedData() {
            return this.paginate(this.sortedData)
        },

        linearName() {
            return `${this.rowName} allocation`
        },

        doughnutName() {
            return `${this.rowName} allocation by source`
        },

        hasUserAccessToMonitoring() {
            return this.getAccessToMonitoring()
        },

        zeroStateMsg() {
            const msg = {
                segment: 'We could not find custom segments on the account',
                audience: 'We could not find audiences with allocation on the account',
                row: 'We could not find any data on the account'
            }

            return msg[this.rowName]
        },

        objectTypes() {
            const objectTypes = {
                segment: 'cs',
                audience: 'audience'
            }

            return objectTypes[this.rowName]
        },

        isAlertCreationAllowed() {
            return this.getCreateAlertsAllowed()
        }
    },

    watch: {
        paginatedData: {
            handler(data) {
                const expandedId = this.expandRowKeys[0]
                const dataById = keyBy(data, 'id')
                if (expandedId === undefined || (isEmpty(dataById[expandedId]?.allocations) && !dataById[expandedId]?.loading)) {
                    this.expandRowKeys = []
                }
            },
            deep: true
        }
    },

    methods: {
        ...mapMutations('ui/monitoring', ['setSelectedRow', 'setActiveTab', 'setIsAudienceCcloneTargeted']),
        ...mapMutations('ui/manageTaxonomy', ['setSelectedSegmentId', 'setSelectedTaxoId']),
        ...mapMutations('dashboard', ['setDisplayAlertForm', 'setAlertInCreation']),

        setRowClassName({ row }) {
            const idClass = `dashboard-tb-row-${row.id}`
            return {
                'dashboard-tb-row': true,
                [idClass]: true,
                'dashboard-tb-row-selectable': true,
                'dashboard-tb-row-deleted': row.deleted,
                'dashboard-tb-row-triggered': row.alerts.some(a => a.isTriggered)
            }
        },

        handleRowClick(row) {
            const id = row.id
            const lastId = this.expandRowKeys[0]
            if (id === lastId) {
                this.expandRowKeys = []
            } else {
                this.expandRowKeys = [id]
                if (isEmpty(this.allocations[id]?.allocations)) {
                    this.$emit('expandRow', row)
                }
            }
        },

        componentHandleSortChange(params) {
            this.currentPage = 1
            this.handleSortChange(params)
        },

        async componentHandleQueryReset() {
            await this.$nextTick()
            this.handleQueryReset(this.$refs.tableData)
        },

        isRowSelectable(row) {
            return !row.deleted
        },

        formatNumber(num) {
            return formatNumber(num)
        },

        goToDefinition(id) {
            if (this.rowName === 'audience') {
                this.setSelectedRow({ rowId: id, from: 'other-page' })
                this.setIsAudienceCcloneTargeted({ value: true })
                this.setActiveTab({ tab: 'information' })
                this.$router.push({ path: '/' + this.accountId + '/monitoring' })
            } else if (this.rowName === 'segment') {
                const taxoId = this.getWamCustomTaxoIdBySegmentId({ id })
                this.setSelectedTaxoId({ id: taxoId.toString() })
                this.setSelectedSegmentId({ id: id.toString() })
                this.$router.push({ path: '/' + this.accountId + '/on-boarding/manage-taxonomy' })
            }
        },

        goToAlertTable(id, triggered = false) {
            this.alertPoperVisible = null
            this.alertTriggeredPoperVisible = null
            this.$nextTick(() => {
                this.$emit('goToAlertTab', {id, triggered})
            })
        },

        async handleFilterQueryChange({ fn, sortByScore }) {
            await this.$nextTick()
            const table = this.$refs.tableData
            if (sortByScore) this.componentHandleSortChange({ prop: 'score', order: 'ascending', table })
            this.queryFilterFn = fn
        },

        openAlertForm(objectId) {
            // Change it to make the interface between api and UI
            const objectType = this.objectTypes === 'cs' ? 'segments' : this.objectTypes
            this.setAlertInCreation({ objectId, objectType })
            this.setDisplayAlertForm({ value: true })
        },

        getTableMaxHeight() {
            return window.innerHeight - 330
        },

        getTriggeredAlerts(item) {
            return item.alerts.filter(alert => alert.isTriggered)
        }
    }
}
</script>
